import React, { useState, useCallback, useMemo, memo } from 'react';
import cn from 'classnames';
import styles from './survey.module.scss';
import { v4 as uuidv4 } from 'uuid';
import SurveyKeyboard from './surveyKeyboard';
import { flowType } from '@utility/userInfo';
import { TouchableButton, TouchableSpan } from '@components/common/Touchable';

const StarIcon = memo(({ isKiosk, isSelected, serverAppTheme }) => (
  <svg
    width={isKiosk ? '200' : '60'}
    height={isKiosk ? '200' : '60'}
    viewBox="0 0 50 50"
    strokeWidth="3"
    stroke={!isSelected ? serverAppTheme?.survey_star_stroke : serverAppTheme?.survey_star_fill}
    fill={isSelected ? serverAppTheme?.survey_star_fill : serverAppTheme?.survey_star_bg}
  >
    <path d="m25,1 6,17h18l-14,11 5,17-15-10-15,10 5-17-14-11h18z" />
  </svg>
));
StarIcon.displayName = 'StarIcon';

const SENTIMENT_STYLES = {
  '-1': {
    border: 'border_red',
    class: 'survey-border-red'
  },
  '0': {
    border: 'border_yellow',
    class: 'survey-border-yellow'
  },
  '1': {
    border: 'border_green',
    class: 'survey-border-green'
  }
};

const SentimentButton = memo(({
  options,
  isSelected,
  isKiosk,
  onSubmit,
  styles: componentStyles,
  index,
  lastIndex,
  serverAppTheme,
  isLandScape
}) => {
  const sentimentConfig = SENTIMENT_STYLES[options.sentiment.toString()];

  if (isKiosk) {
    return (
      <div className="d-flex justify-content-center align-items-center col-8 offset-2">
        <TouchableButton
          className={cn(
            componentStyles[sentimentConfig.border],
            sentimentConfig.class,
            isSelected ? componentStyles.btnKiosk_square_selected : componentStyles.btnKiosk_square,
            'fs_16 fw-600'
          )}
          type="button"
          onClick={() => onSubmit(options)}
        >
          {options.value}
        </TouchableButton>
        {(index === 0 || lastIndex === index) ? (
          <div className="col-9" style={{ fontSize: '45px' }}>
            {options.name}
          </div>
        ) : (
          <div className="col-9" />
        )}
      </div>
    );
  }

  return (
    <div className={cn("d-flex justify-content-center align-items-center px-0", isLandScape ? 'col flex-column' : 'col-12')}>
      <div className="col-5 px-0" />
      <div className="col-2 px-0">
        <TouchableButton
          className={cn(
            componentStyles[sentimentConfig.border],
            sentimentConfig.class,
            'text-center border-0 fs_28 fw-600',
            isSelected ? componentStyles.btn_selected : componentStyles.btnNot_selected
          )}
          type="button"
          onClick={() => onSubmit(options)}
        >
          {options.value}
        </TouchableButton>
      </div>
      {(lastIndex === index || index === 0) ? (
        <div className={cn("col-5 px-0 text-left")} style={{ color: serverAppTheme?.secondary_text, left: isLandScape && index === 0 && '10px'}}>
          <span className={cn(isLandScape && "text-nowrap")} style={{marginLeft: isLandScape && lastIndex === index && '-77px'}}>{options.name}</span>
        </div>
      ) : (
        <div className="col-5 px-0" />
      )}
    </div>
  );
});
SentimentButton.displayName = 'SentimentButton';

const YesNoButton = memo(({ options, onSubmit, serverAppTheme, isLandScape }) => {
  const optionName = options.name.toLowerCase();
  const isNoButton = optionName === 'no' || optionName === 'never';
  const isYesButton = optionName === 'yes' || optionName === 'absolutely';

  if (isNoButton) {
    return (
      <div className="float-left pt-3 px-0 col-12">
        <TouchableButton
          style={{
            border: '1px solid #2541A4',
            background: serverAppTheme?.OFF_Premise_Btn,
            color: serverAppTheme?.cancleBtn_font,
            height: isLandScape ? '3.5rem' : '5.014rem',
            fontWeight: '600'
          }}
          className="btn90 rounded my-1 w-100 fs_32 btn70"
          onClick={() => onSubmit(options)}
          rippleEffect={true}
        >
          No
        </TouchableButton>
      </div>
    );
  }

  if (isYesButton) {
    return (
      <div className="col-12 px-0">
        <TouchableButton
          className="btn90 rounded my-1 btn-ripple w-100 fs_32"
          style={{
            background: serverAppTheme?.secondaryBtn,
            color: serverAppTheme?.btn_font_color,
            height: isLandScape ? '3.5rem' :'5.014rem',
            fontWeight: '600'
          }}
          onClick={() => onSubmit(options)}
          rippleEffect={true}
        >
          Yes
        </TouchableButton>
      </div>
    );
  }

  return null;
});
YesNoButton.displayName = 'YesNoButton';

const SurveyStar = memo(({
  selectedOption,
  questionType,
  options,
  index,
  lastIndex,
  isSelected,
  note,
  serverAppTheme,
  isLandScape
}) => {
  const isKiosk = useMemo(() => flowType() === 'kiosk', []);

  const [feedback, setFeedback] = useState(note);
  const [isFeedback, setIsFeedback] = useState(false);
  const [isInitial, setIsInitial] = useState(false);

  const handleSubmitOption = useCallback((data, questiontype, option) => {
    selectedOption(data, questiontype, option);
  }, [selectedOption]);

  const handleFeedback = useCallback((value) => {
    if (isInitial) {
      setFeedback(prev => prev.concat(value));
      setIsInitial(false);
    } else {
      setFeedback(value);
    }
  }, [isInitial]);

  const handleClose = useCallback(() => {
    setIsInitial(true);
    setIsFeedback(false);
  }, []);

  const handleOpenKeyboard = useCallback(() => {
    setIsFeedback(true);
  }, []);

  const handleStarClick = useCallback(() => {
    selectedOption(options);
  }, [selectedOption, options]);
  

  const handleOptionClick = useCallback(() => {
    selectedOption(options);
  }, [selectedOption, options]);

  const handleSentimentClick = useCallback(() => {
    handleSubmitOption(options);
  }, [handleSubmitOption, options]);

  const uniqueKey = useMemo(() => uuidv4(), []);

  const type3ButtonStyle = useMemo(() => ({
    border: `2px solid ${serverAppTheme?.survey_select_border}`,
    background: isSelected ? serverAppTheme?.primaryBtn : serverAppTheme?.tip_tile,
    borderRadius: '12px',
    color: isSelected ? '#fff' : serverAppTheme?.primary_text
  }), [isSelected, serverAppTheme]);

  const renderQuestionType = useMemo(() => {
    switch (questionType) {
      case 1:
        return (
          <TouchableSpan
            key={uniqueKey}
            className={cn('col icon-od text-center outline-0 fs-26 px-1',styles.star)}
            tabIndex={index}
            role="button"
            onKeyDown={handleStarClick}
            onClick={handleStarClick}
          >
            <StarIcon
              isKiosk={isKiosk}
              isSelected={isSelected}
              serverAppTheme={serverAppTheme}
            />
          </TouchableSpan>
        );

      case 2:
        return (
          <div className="col-12 px-0">
            <YesNoButton
              options={options}
              onSubmit={handleOptionClick}
              serverAppTheme={serverAppTheme}
              isLandScape={isLandScape}
            />
          </div>
        );

      case 3:
        return (
          <div className="d-flex justify-content-center">
            <TouchableButton
              className={cn('text-left',isLandScape ? `my-2 ${styles.landscapeheight69}` : `my-1 ${styles.height80}`, 'btn w-100 fs_28')}
              type="button"
              onClick={handleOptionClick}
              style={type3ButtonStyle}
              rippleEffect={true}
            >
              <div className="fs_18 fw-600 pl-2 text-center">{options.name}</div>
            </TouchableButton>
          </div>
        );

      case 4:
        return (
          <SentimentButton
            options={options}
            isSelected={isSelected}
            isKiosk={isKiosk}
            onSubmit={handleSentimentClick}
            styles={styles}
            index={index}
            lastIndex={lastIndex}
            serverAppTheme={serverAppTheme}
            isLandScape={isLandScape}
          />
        );

      case 9:
        return (
          <div className={cn(isLandScape ? "h-98" : "h-100")}>
            <SurveyKeyboard
              type="text"
              name="surveyFeedback"
              handleFeedback={handleFeedback}
              selectedOption={selectedOption}
              feedback={feedback}
              options={options}
              isKiosk={isKiosk}
              isFeedback={isFeedback}
              onClose={handleClose}
              handleOpenKeyboard={handleOpenKeyboard}
              serverAppTheme={serverAppTheme}
            />
          </div>
        );

      default:
        return null;
    }
  }, [
    questionType,
    uniqueKey,
    index,
    handleStarClick,
    isKiosk,
    isSelected,
    serverAppTheme,
    options,
    handleOptionClick,
    type3ButtonStyle,
    handleSentimentClick,
    lastIndex,
    handleFeedback,
    selectedOption,
    feedback,
    isFeedback,
    handleClose,
    handleOpenKeyboard
  ]);

  return <>{renderQuestionType}</>;
});

SurveyStar.displayName = 'SurveyStar';

export default SurveyStar;